import { arc, rule, tiledRule } from './geometry';
import { INK, INK_FAINT, INK_STRONG } from './ink';
import { BEAM_MASK, OVAL_MASK, RING_MASK } from './masks';
import { type Paint, type SectionPatternName } from './types';

/**
 * The motifs themselves — what each one is made of and which band it is for.
 *
 * Everything here is paint and nothing here is content: no request, no image,
 * no JavaScript, no animation, and no node per motif. A patterned band costs
 * one absolutely positioned `<div>` that the compositor rasterises once.
 *
 * Three rules hold the set together. **Major and minor**: a single-weight
 * motif at an alpha low enough to stay out of the way reads as a screen
 * artefact rather than as a decision, so every geometric pattern pairs a
 * heavier rule with a lighter one. **Nothing reaches an edge** — see
 * `masks.ts`. **Density is proportional**: no recipe states a period, it
 * states which period it uses, and `scale.ts` decides what that is for the box
 * being painted. Majors are derived from the minor here rather than tabulated
 * there, so a step cannot define the two inconsistently.
 */
const GRID = 'var(--kabex-pattern-grid)';
const COLUMN = 'var(--kabex-pattern-column)';
const ISO = 'var(--kabex-pattern-iso)';
const DOT = 'var(--kabex-pattern-dot)';
const CROSS = 'var(--kabex-pattern-cross)';
const RING = 'var(--kabex-pattern-ring)';
const DIAGONAL = 'var(--kabex-pattern-diagonal)';
const HATCH = 'var(--kabex-pattern-hatch)';

/** A heavy rule every fourth light one — the set's standard major interval. */
const major = (period: string) => `calc(${period} * 4)`;

/**
 * A mark sized as a share of its own lattice, so a dot stays the same dot at
 * every step instead of swelling as the spacing tightens. The shares are the
 * tuned px values divided by the baseline period: `0.077 × 26px` is the 2px
 * dot the band step has always drawn.
 */
const share = (period: string, of: number) => `calc(${period} * ${of})`;

/** Half a survey mark's stroke: a weight, so it is a length and never a share. */
const CROSS_STROKE = '0.75px';

export const recipes: Record<SectionPatternName, Paint> = {
  /**
   * Blueprint rule: a fine grid with a heavy line every fourth cell. For bands
   * whose own content is already a grid — the motif reads as the ruling the
   * tiles were set out on rather than as wallpaper behind them.
   */
  grid: {
    backgroundImage: [
      tiledRule('to bottom', INK),
      tiledRule('to right', INK),
      tiledRule('to bottom', INK_FAINT),
      tiledRule('to right', INK_FAINT),
    ].join(','),
    backgroundSize: [
      `${major(GRID)} ${major(GRID)}`,
      `${major(GRID)} ${major(GRID)}`,
      `${GRID} ${GRID}`,
      `${GRID} ${GRID}`,
    ].join(','),
    maskImage: OVAL_MASK,
  },

  /**
   * Uprights: a vertical rule with a heavy one every fourth, and no
   * horizontals at all — the only motif in the set that runs on one axis.
   *
   * For the band the logos scroll across. The rules stay put while the wall
   * slides over them, and it is having something stationary to move against
   * that makes the motion read as motion rather than as a row that will not
   * sit still. A grid would do the same job and also fence every logo into a
   * box, which is the one thing a roster of other people's brands should not
   * do.
   *
   * Sized `100%` in the vertical rather than to a tile: a rule that runs on
   * one axis is uniform down the other, so a repeating tile would rasterise
   * the same column of pixels over and over for nothing.
   */
  columns: {
    backgroundImage: [tiledRule('to right', INK), tiledRule('to right', INK_FAINT)].join(','),
    backgroundSize: `${major(COLUMN)} 100%, ${COLUMN} 100%`,
    maskImage: OVAL_MASK,
  },

  /**
   * An isometric lattice: three families of rules at 0°, 60° and 120° on one
   * period, tiling into equilateral triangles.
   *
   * The only motif in the set that implies a third dimension, and the only one
   * off the 45/90 axis everything else is drawn on — which is what keeps it
   * from reading as another variation on the ruling two bands up. It is the
   * projection an engineering drawing is set out in, and this is the band that
   * argues the company is thorough, so the backdrop makes the same case the
   * copy does.
   *
   * Three families at once is a lot of ink, and the temptation is to answer
   * that by thinning it. That is the mistake the crosshatch made. The period
   * carries the density instead — the widest spacing in the set, which lands
   * this at roughly the coverage of the crosshatch.
   */
  isometric: {
    backgroundImage: [
      rule('0deg', ISO, INK),
      rule('60deg', ISO, INK),
      rule('-60deg', ISO, INK),
    ].join(','),
    maskImage: OVAL_MASK,
  },

  /**
   * A staggered dot field: a heavier dot on a lattice with a lighter one
   * offset into each gap. For bands whose content is deliberately irregular —
   * straight ruling behind a bento of mismatched cells fights the layout,
   * where a dot field simply gives it a surface to sit on.
   *
   * Drawn in `strong` ink. The first version of this used a 1.4px dot in the
   * same ink as the rules and was invisible on the page: two hundredths of a
   * percent of the band carried the motif, which the eye averages away
   * entirely. Area, not alpha, is what a dot field is short of.
   *
   * The stops are feathered rather than hard, because a hard stop on a radial
   * gradient is what makes CSS dots look ragged at fractional device pixel
   * ratios — the reasoning `geometry.ts` now applies to the whole set.
   */
  dots: {
    backgroundImage: [
      `radial-gradient(circle at center, ${INK_STRONG} 0 ${share(DOT, 0.077)}, transparent ${share(DOT, 0.0923)})`,
      `radial-gradient(circle at center, ${INK} 0 ${share(DOT, 0.054)}, transparent ${share(DOT, 0.069)})`,
    ].join(','),
    backgroundSize: `${DOT} ${DOT}, ${DOT} ${DOT}`,
    backgroundPosition: `0 0, calc(${DOT} / 2) calc(${DOT} / 2)`,
    maskImage: OVAL_MASK,
  },

  /**
   * Survey marks: a small plus at every node of a lattice. The dot field with
   * a direction added to it — it reads as something set out and measured
   * rather than as texture, which suits a band that claims every step is
   * handled in-house.
   *
   * Each arm is a very flat ellipse rather than a line, because a gradient is
   * uniform along the axis it does not run on: one linear layer can bound an
   * arm's length or its thickness, never both, so a true cross cannot be built
   * from them. A radial is bounded in both axes, and at 0.75px the rounded ends
   * are well under a pixel.
   *
   * The arm's *length* scales with the lattice and its *thickness* does not,
   * which is the one place in this file where the two axes of a mark part
   * company. A cross is a stroked glyph, not a filled one: scaling its weight
   * with its spacing would take the arm to under half a pixel at the tight
   * step, where a sub-pixel stroke is composited to a fraction of its already
   * low alpha and the mark disappears. The dots opposite can scale wholesale
   * because a disc's weight *is* its area, and holding its share of the
   * lattice constant is exactly what keeps the field's density even.
   */
  crosses: {
    backgroundImage: [
      `radial-gradient(ellipse ${share(CROSS, 0.147)} ${CROSS_STROKE} at 50% 50%, ${INK_STRONG} 0 70%, transparent 100%)`,
      `radial-gradient(ellipse ${CROSS_STROKE} ${share(CROSS, 0.147)} at 50% 50%, ${INK_STRONG} 0 70%, transparent 100%)`,
    ].join(','),
    backgroundSize: `${CROSS} ${CROSS}, ${CROSS} ${CROSS}`,
    maskImage: OVAL_MASK,
  },

  /**
   * Ripples from a light source just above the band: concentric arcs with a
   * heavy ring every fourth. The only curved motif in the set, which is what
   * keeps a band of vehicle cards from reading as more of the same ruling as
   * the bands either side of it — and it restates the hero's one idea, that
   * the cars are lit from above rather than sitting on a flat backdrop.
   *
   * ## The origin is a length, not a percentage
   *
   * The centre sits off the top edge so the band shows the arcs spreading,
   * never the bullseye they come from. That used to be `-8%`, which on a
   * `circle at` position resolves against the band's *height* — and band
   * heights are the least predictable number on the site, because the same
   * band is short and wide on a desktop and tall and narrow once its content
   * stacks on a phone. The first arc that reaches the band has a radius equal
   * to that offset, so a short band put a tight 40px arc at the top centre and
   * showed exactly the bullseye the recipe promised it never would, while a
   * tall one pushed the centre 140px out and flattened the arcs to nothing.
   *
   * At 1.5 periods out it is 60px at the baseline step — what `-8%` gave a
   * 750px band, which is about what a desktop band measures — and it is now
   * the same 1.5 periods on every screen. Tied to the period rather than
   * fixed, so the offset and the arc spacing scale together and the curvature
   * the visitor sees is the one thing that stays constant.
   */
  rings: {
    backgroundImage: [
      arc(`50% calc(${RING} * -1.5)`, major(RING), INK),
      arc(`50% calc(${RING} * -1.5)`, RING, INK_FAINT),
    ].join(','),
    maskImage: OVAL_MASK,
  },

  /**
   * Rising hairlines, again at two weights, with a major rule every fifth.
   *
   * For a band that scrolls sideways — the slant gives a static row of cards a
   * direction, which is the thing that suggests there is more of it offscreen.
   */
  diagonals: {
    backgroundImage: [
      rule('135deg', `calc(${DIAGONAL} * 5)`, INK),
      rule('135deg', DIAGONAL, INK_FAINT),
    ].join(','),
    maskImage: OVAL_MASK,
  },

  /**
   * Graph paper: the same hairline at +45° and -45°.
   *
   * Crossing two rulings doubles the ink on the surface, so the first version
   * answered that by halving the alpha — and between the faint ink and the
   * ring mask's ceiling the result measured at a third the weight of every
   * other pattern, which is to say it was not there. The density is paid for
   * in the period instead: the standard ink spaced wider, rather than the
   * faint ink spaced tighter. Spacing is the honest lever for a motif that is
   * too busy; alpha just makes it too busy and invisible at once.
   *
   * Masked to a ring, so the texture frames the band and leaves its middle —
   * where the form fields are — a plain surface.
   */
  crosshatch: {
    backgroundImage: [rule('45deg', HATCH, INK), rule('-45deg', HATCH, INK)].join(','),
    maskImage: RING_MASK,
  },

  /**
   * Light thrown from behind the form: forty fine rays converging on it, and
   * a warm bloom in the brand red at the same point.
   *
   * Every other motif here is an even texture — it gives a band a surface and
   * says nothing about where to look. This band is the one place on the page
   * that asks for something, so its backdrop is the one that is directional.
   * The rays widen as they travel, the way real ones do, because a conic
   * gradient's arc-width grows with distance from the origin; that is what
   * makes them read as light rather than as a fan of drawn lines. They are the
   * one family in the set spaced in degrees rather than pixels, so they take
   * no step from `scale.ts` — a ray's width already scales with the band,
   * which is the whole property that makes a conic gradient right here.
   *
   * Where they converge is `masks.ts`, and it is the one number in this module
   * that was measured against the wrong box.
   *
   * The bloom is the only place a pattern uses the brand colour on a light
   * band. On a wide band it sits behind an opaque card and touches no text at
   * all; on a phone the origin moves up into the copy, and measured there it
   * costs the worst-affected line 0.18 of its contrast ratio — 6.89 to 6.71 in
   * light, 6.49 to 6.41 in dark, against a requirement of 4.5. It is not free,
   * it is just cheap, and 0.16 is the alpha that keeps it that way.
   *
   * It follows the colour scheme through `primary-mainChannel`, which is the
   * right behaviour here — unlike `aurora`, this band is genuinely light in
   * light mode and dark in dark mode.
   */
  beams: {
    backgroundImage: `repeating-conic-gradient(from 4deg at var(--kabex-beam-origin), ${INK} 0deg 0.4deg, transparent 0.9deg 9deg)`,
    maskImage: BEAM_MASK,
    glow: `radial-gradient(46% 62% at var(--kabex-beam-origin), rgba(var(--mui-palette-primary-mainChannel) / 0.16) 0%, rgba(var(--mui-palette-primary-mainChannel) / 0.05) 42%, rgba(var(--mui-palette-primary-mainChannel) / 0) 74%)`,
  },

  /**
   * The inverse-tone half of the set: not a motif but a light source. Two
   * brand-red fields off opposite corners and a cool lift through the middle,
   * so a dark closing band has a direction of light instead of one flat value
   * of black — the hero's ambient mesh, held still.
   *
   * The reds are literals rather than `--mui-palette-primary-mainChannel`
   * because a `tone="inverse"` band is near-black in *both* schemes, so a
   * scheme-following red would change underneath a surface that did not.
   * It needs no mask: three wide radials have no edge to hide, and no step:
   * a wash has no period to scale.
   */
  aurora: {
    backgroundImage: [
      'radial-gradient(58% 68% at 10% 0%, rgba(197,61,73,0.20) 0%, rgba(197,61,73,0) 70%)',
      'radial-gradient(50% 62% at 90% 100%, rgba(197,61,73,0.13) 0%, rgba(197,61,73,0) 68%)',
      'radial-gradient(44% 54% at 50% 46%, rgba(158,170,190,0.10) 0%, rgba(158,170,190,0) 72%)',
    ].join(','),
  },
};
